import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Copy, Check } from 'lucide-react';
import { useCallback } from 'react';
import categoriesColorList from '@/utils/categoriesColorList';
import useCopyState from './useCopyState';

interface ArtifactChartBlockProps {
  title: string;
  data: {
    x_label?: string;
    y_label?: string;
    series: Array<{ name: string; value: number; color?: string }>;
  };
}

const DEFAULT_COLORS = categoriesColorList;

function ArtifactChartBlock({ title, data }: ArtifactChartBlockProps) {
  const [copied, triggerCopied] = useCopyState();

  const handleCopy = useCallback(() => {
    const text = data.series.map((s) => `${s.name}\t${s.value}`).join('\n');
    navigator.clipboard
      .writeText(`${title}\n${data.x_label || ''}\t${data.y_label || ''}\n${text}`)
      .then(() => triggerCopied())
      .catch(() => {});
  }, [data, title, triggerCopied]);

  if (!data?.series?.length) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>
        <button
          type="button"
          onClick={handleCopy}
          className="p-1.5 text-neutral-400 hover:text-neutral-600 rounded hover:bg-neutral-100 transition-colors"
          title="Copy data"
          aria-label="Copy chart data"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-primary" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      <div className="bg-white border border-neutral-200 rounded-lg p-4">
        <ResponsiveContainer width="100%" height={Math.max(200, data.series.length * 35)}>
          <BarChart
            data={data.series}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              type="number"
              tick={{ fontSize: 11, fill: '#737373' }}
              label={
                data.y_label
                  ? { value: data.y_label, position: 'insideBottom', offset: -5, fontSize: 11 }
                  : undefined
              }
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 11, fill: '#525252' }}
              width={120}
            />
            <Tooltip
              contentStyle={{
                borderRadius: 8,
                border: '1px solid #e5e5e5',
                fontSize: 12,
              }}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {data.series.map((entry, idx) => (
                <Cell
                  key={entry.name}
                  fill={entry.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default ArtifactChartBlock;
